import { useState, useEffect } from 'react';
import type { FC } from 'react';
import type { Task, Zone, Driver, Truck, TaskType, TaskPriority } from '../types';
import { useCompanyData } from '../contexts/CompanyDataContext';
import './DispatchPanel.css';

interface DispatchPanelProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  zones: Zone[];
  drivers: Driver[];
  trucks: Truck[];
  onTaskCreate: (task: Partial<Task>) => Promise<void>;
  onTaskAssign: (taskId: string, driverId: string) => Promise<void>;
  onTaskAutoAssign: (taskId: string) => Promise<void>;
  onTaskCancel: (taskId: string) => Promise<void>;
  onTaskSelect: (task: Task | null) => void;
  selectedTask: Task | null;
}

const taskTypeLabels: Record<TaskType, string> = {
  pallet: '📦 Pall-flytt',
  return: '↩️ Retur',
  loading_dock: '🚚 Lastkaj',
  charging: '🔋 Laddning',
  internal_move: '🔄 Intern flytt',
  pick: '🛒 Plock',
  putaway: '📥 Inlagring',
};

const priorityLabels: Record<TaskPriority, { label: string; color: string }> = {
  low: { label: 'Låg', color: 'var(--color-text-muted)' },
  normal: { label: 'Normal', color: 'var(--color-accent)' },
  high: { label: 'Hög', color: 'var(--color-warning)' },
  urgent: { label: 'Brådskande', color: 'var(--color-danger)' },
};

type LocationType = 'zone' | 'warehouse' | 'customer' | 'custom';

interface LocationOption {
  type: LocationType;
  id: string;
  name: string;
  address?: string;
  latitude?: number;
  longitude?: number;
}

const DispatchPanel: FC<DispatchPanelProps> = ({
  isOpen,
  onClose,
  tasks,
  zones,
  drivers,
  trucks: _trucks,
  onTaskCreate,
  onTaskAssign,
  onTaskAutoAssign,
  onTaskCancel,
  onTaskSelect,
  selectedTask,
}) => {
  const { warehouses, customers } = useCompanyData();
  const [activeTab, setActiveTab] = useState<'create' | 'queue' | 'active' | 'history'>('queue');
  const [isCreating, setIsCreating] = useState(false);
  
  // Location source selectors
  const [pickupSource, setPickupSource] = useState<LocationType>('zone');
  const [dropoffSource, setDropoffSource] = useState<LocationType>('zone');
  
  // Create task form state
  const [newTask, setNewTask] = useState({
    type: 'internal_move' as TaskType,
    priority: 'normal' as TaskPriority,
    pickupId: '',
    pickupAisle: '',
    pickupShelf: '',
    dropoffId: '',
    dropoffAisle: '',
    dropoffShelf: '',
    description: '',
    requiredTruckType: '',
    // Custom location fields
    customPickupName: '',
    customPickupAddress: '',
    customDropoffName: '',
    customDropoffAddress: '',
  });

  // Build location options based on source type
  const getLocationOptions = (source: LocationType): LocationOption[] => {
    switch (source) {
      case 'zone':
        return zones.map(z => ({
          type: 'zone' as const,
          id: z.id,
          name: z.name,
          latitude: z.latitude,
          longitude: z.longitude,
        }));
      case 'warehouse':
        return warehouses.map(w => ({
          type: 'warehouse' as const,
          id: w.id,
          name: w.name,
          address: w.address,
          latitude: w.latitude,
          longitude: w.longitude,
        }));
      case 'customer':
        return customers.map(c => ({
          type: 'customer' as const,
          id: c.id,
          name: c.name,
          address: c.address,
          latitude: c.latitude,
          longitude: c.longitude,
        }));
      default:
        return [];
    }
  };

  const pickupOptions = getLocationOptions(pickupSource);
  const dropoffOptions = getLocationOptions(dropoffSource);

  const pendingTasks = tasks.filter(t => t.status === 'pending');
  const activeTasks = tasks.filter(t => ['assigned', 'accepted', 'in_progress', 'at_pickup', 'loaded', 'in_transit', 'at_dropoff'].includes(t.status));
  const completedTasks = tasks.filter(t => ['completed', 'failed', 'cancelled'].includes(t.status));

  // Reset location selection when source changes
  useEffect(() => {
    setNewTask(prev => ({ ...prev, pickupId: '' }));
  }, [pickupSource]);

  useEffect(() => {
    setNewTask(prev => ({ ...prev, dropoffId: '' }));
  }, [dropoffSource]);

  const handleCreateTask = async () => {
    // Validation
    if (pickupSource === 'custom') {
      if (!newTask.customPickupName || !newTask.customPickupAddress) return;
    } else if (!newTask.pickupId) return;

    if (dropoffSource === 'custom') {
      if (!newTask.customDropoffName || !newTask.customDropoffAddress) return;
    } else if (!newTask.dropoffId) return;

    setIsCreating(true);
    try {
      // Build pickup location
      let pickupLocation;
      if (pickupSource === 'custom') {
        pickupLocation = {
          zoneId: 'custom',
          zoneName: newTask.customPickupName,
          latitude: 0,
          longitude: 0,
          label: `${newTask.customPickupName} - ${newTask.customPickupAddress}`,
        };
      } else {
        const pickupOption = getLocationOptions(pickupSource).find(o => o.id === newTask.pickupId);
        pickupLocation = {
          zoneId: newTask.pickupId,
          zoneName: pickupOption?.name,
          aisle: newTask.pickupAisle || undefined,
          shelf: newTask.pickupShelf || undefined,
          latitude: pickupOption?.latitude || 0,
          longitude: pickupOption?.longitude || 0,
          label: buildLocationLabel(pickupOption, newTask.pickupAisle, newTask.pickupShelf),
        };
      }

      // Build dropoff location
      let dropoffLocation;
      if (dropoffSource === 'custom') {
        dropoffLocation = {
          zoneId: 'custom',
          zoneName: newTask.customDropoffName,
          latitude: 0,
          longitude: 0,
          label: `${newTask.customDropoffName} - ${newTask.customDropoffAddress}`,
        };
      } else {
        const dropoffOption = getLocationOptions(dropoffSource).find(o => o.id === newTask.dropoffId);
        dropoffLocation = {
          zoneId: newTask.dropoffId,
          zoneName: dropoffOption?.name,
          aisle: newTask.dropoffAisle || undefined,
          shelf: newTask.dropoffShelf || undefined,
          latitude: dropoffOption?.latitude || 0,
          longitude: dropoffOption?.longitude || 0,
          label: buildLocationLabel(dropoffOption, newTask.dropoffAisle, newTask.dropoffShelf),
        };
      }

      await onTaskCreate({
        type: newTask.type,
        priority: newTask.priority,
        pickup: pickupLocation,
        dropoff: dropoffLocation,
        description: newTask.description || undefined,
        requiredTruckType: (newTask.requiredTruckType as Task['requiredTruckType']) || undefined,
      });

      // Reset form
      setNewTask({
        type: 'internal_move',
        priority: 'normal',
        pickupId: '',
        pickupAisle: '',
        pickupShelf: '',
        dropoffId: '',
        dropoffAisle: '',
        dropoffShelf: '',
        description: '',
        requiredTruckType: '',
        customPickupName: '',
        customPickupAddress: '',
        customDropoffName: '',
        customDropoffAddress: '',
      });
      setPickupSource('zone');
      setDropoffSource('zone');
      setActiveTab('queue');
    } catch (error) {
      console.error('Failed to create task:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const buildLocationLabel = (option: LocationOption | undefined, aisle?: string, shelf?: string) => {
    if (!option) return '';
    let label = option.name;
    if (option.address) label += ` (${option.address})`;
    if (aisle) label += ` - Gång ${aisle}`;
    if (shelf) label += ` - Hylla ${shelf}`;
    return label;
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      pending: 'Väntar',
      assigned: 'Tilldelad',
      accepted: 'Accepterad',
      in_progress: 'Pågår',
      at_pickup: 'Vid upphämtning',
      loaded: 'Lastad',
      in_transit: 'På väg',
      at_dropoff: 'Vid leverans',
      completed: 'Slutförd',
      failed: 'Misslyckad',
      cancelled: 'Avbruten',
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: 'var(--color-text-muted)',
      assigned: 'var(--color-accent)',
      accepted: 'var(--color-accent)',
      in_progress: 'var(--color-success)',
      at_pickup: 'var(--color-warning)',
      loaded: 'var(--color-success)',
      in_transit: 'var(--color-success)',
      at_dropoff: 'var(--color-warning)',
      completed: 'var(--color-success)',
      failed: 'var(--color-danger)',
      cancelled: 'var(--color-text-muted)',
    };
    return colors[status] || 'var(--color-text-muted)';
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just nu';
    if (diffMins < 60) return `${diffMins} min`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} tim`;
    return date.toLocaleDateString('sv-SE');
  };

  const availableDrivers = drivers.filter(d => d.isTracking && !d.currentTaskId && d.status !== 'offline');

  const sourceLabels: Record<LocationType, string> = {
    zone: 'Zon',
    warehouse: 'Lager',
    customer: 'Kund',
    custom: 'Egen',
  };

  if (!isOpen) return null;

  return (
    <div className="dispatch-overlay">
      <div className="dispatch-panel">
        <div className="dispatch-header">
          <div className="dispatch-header-left">
            <div className="dispatch-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                <path d="M16 7V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v3"/>
                <line x1="12" y1="12" x2="12" y2="16"/>
                <line x1="10" y1="14" x2="14" y2="14"/>
              </svg>
            </div>
            <div>
              <h2>Dispatch Center</h2>
              <p className="dispatch-subtitle">{pendingTasks.length} väntande · {activeTasks.length} aktiva</p>
            </div>
          </div>
          <button className="dispatch-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="dispatch-tabs">
          <button 
            className={`dispatch-tab ${activeTab === 'create' ? 'active' : ''}`}
            onClick={() => setActiveTab('create')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            <span>Skapa</span>
          </button>
          <button 
            className={`dispatch-tab ${activeTab === 'queue' ? 'active' : ''}`}
            onClick={() => setActiveTab('queue')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="8" y1="6" x2="21" y2="6"/>
              <line x1="8" y1="12" x2="21" y2="12"/>
              <line x1="8" y1="18" x2="21" y2="18"/>
              <circle cx="4" cy="6" r="1"/>
              <circle cx="4" cy="12" r="1"/>
              <circle cx="4" cy="18" r="1"/>
            </svg>
            <span>Kö ({pendingTasks.length})</span>
          </button>
          <button 
            className={`dispatch-tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <polyline points="12 6 12 12 16 14"/>
            </svg>
            <span>Aktiva ({activeTasks.length})</span>
          </button>
          <button 
            className={`dispatch-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 3v5h5"/>
              <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
            </svg>
            <span>Historik</span>
          </button>
        </div>

        <div className="dispatch-content">
          {activeTab === 'create' && (
            <div className="create-task-form">
              {/* Task Type & Priority */}
              <div className="form-section">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <line x1="16" y1="13" x2="8" y2="13"/>
                    <line x1="16" y1="17" x2="8" y2="17"/>
                  </svg>
                  Uppdragstyp & Prioritet
                </h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Typ</label>
                    <select 
                      value={newTask.type}
                      onChange={(e) => setNewTask({ ...newTask, type: e.target.value as TaskType })}
                    >
                      {Object.entries(taskTypeLabels).map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Prioritet</label>
                    <select 
                      value={newTask.priority}
                      onChange={(e) => setNewTask({ ...newTask, priority: e.target.value as TaskPriority })}
                    >
                      {Object.entries(priorityLabels).map(([value, { label }]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Pickup Location */}
              <div className="form-section location-section">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                  Upphämtning (Från)
                </h4>
                
                {/* Source Type Selector */}
                <div className="source-selector">
                  {(['zone', 'warehouse', 'customer', 'custom'] as LocationType[]).map(type => (
                    <button
                      key={type}
                      type="button"
                      className={`source-btn ${pickupSource === type ? 'active' : ''}`}
                      onClick={() => setPickupSource(type)}
                    >
                      {sourceLabels[type]}
                    </button>
                  ))}
                </div>

                {pickupSource === 'custom' ? (
                  <div className="custom-location-inputs">
                    <div className="form-group">
                      <label>Namn</label>
                      <input
                        type="text"
                        placeholder="T.ex. Extern leverantör"
                        value={newTask.customPickupName}
                        onChange={(e) => setNewTask({ ...newTask, customPickupName: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Adress</label>
                      <input
                        type="text"
                        placeholder="Gatuadress, Stad"
                        value={newTask.customPickupAddress}
                        onChange={(e) => setNewTask({ ...newTask, customPickupAddress: e.target.value })}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label>{sourceLabels[pickupSource]}</label>
                      <select 
                        value={newTask.pickupId}
                        onChange={(e) => setNewTask({ ...newTask, pickupId: e.target.value })}
                      >
                        <option value="">Välj {sourceLabels[pickupSource].toLowerCase()}...</option>
                        {pickupOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.name} {option.address ? `(${option.address})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {pickupSource === 'zone' && (
                      <>
                        <div className="form-group">
                          <label>Gång</label>
                          <input 
                            type="text"
                            placeholder="t.ex. A"
                            value={newTask.pickupAisle}
                            onChange={(e) => setNewTask({ ...newTask, pickupAisle: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Hylla</label>
                          <input 
                            type="text"
                            placeholder="t.ex. 14"
                            value={newTask.pickupShelf}
                            onChange={(e) => setNewTask({ ...newTask, pickupShelf: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Dropoff Location */}
              <div className="form-section location-section">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--color-danger)" strokeWidth="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                  Leverans (Till)
                </h4>
                
                {/* Source Type Selector */}
                <div className="source-selector">
                  {(['zone', 'warehouse', 'customer', 'custom'] as LocationType[]).map(type => (
                    <button
                      key={type}
                      type="button"
                      className={`source-btn ${dropoffSource === type ? 'active' : ''}`}
                      onClick={() => setDropoffSource(type)}
                    >
                      {sourceLabels[type]}
                    </button>
                  ))}
                </div>

                {dropoffSource === 'custom' ? (
                  <div className="custom-location-inputs">
                    <div className="form-group">
                      <label>Namn</label>
                      <input
                        type="text"
                        placeholder="T.ex. Ny kund"
                        value={newTask.customDropoffName}
                        onChange={(e) => setNewTask({ ...newTask, customDropoffName: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Adress</label>
                      <input
                        type="text"
                        placeholder="Gatuadress, Stad"
                        value={newTask.customDropoffAddress}
                        onChange={(e) => setNewTask({ ...newTask, customDropoffAddress: e.target.value })}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="form-row">
                    <div className="form-group flex-2">
                      <label>{sourceLabels[dropoffSource]}</label>
                      <select 
                        value={newTask.dropoffId}
                        onChange={(e) => setNewTask({ ...newTask, dropoffId: e.target.value })}
                      >
                        <option value="">Välj {sourceLabels[dropoffSource].toLowerCase()}...</option>
                        {dropoffOptions.map(option => (
                          <option key={option.id} value={option.id}>
                            {option.name} {option.address ? `(${option.address})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    {dropoffSource === 'zone' && (
                      <>
                        <div className="form-group">
                          <label>Gång</label>
                          <input 
                            type="text"
                            placeholder="t.ex. B"
                            value={newTask.dropoffAisle}
                            onChange={(e) => setNewTask({ ...newTask, dropoffAisle: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Hylla</label>
                          <input 
                            type="text"
                            placeholder="t.ex. 7"
                            value={newTask.dropoffShelf}
                            onChange={(e) => setNewTask({ ...newTask, dropoffShelf: e.target.value })}
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Quick Select Info */}
              {(warehouses.length > 0 || customers.length > 0) && (
                <div className="info-banner">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 16v-4"/>
                    <path d="M12 8h.01"/>
                  </svg>
                  <span>
                    Du kan välja bland {warehouses.length} lager och {customers.length} kunder från onboarding
                  </span>
                </div>
              )}

              {/* Details */}
              <div className="form-section">
                <h4>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                  Detaljer (valfritt)
                </h4>
                <div className="form-group">
                  <label>Beskrivning</label>
                  <textarea 
                    placeholder="t.ex. 3 pallar med livsmedel, kontakta förare vid ankomst"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>

              <button 
                className="btn btn-create-task"
                onClick={handleCreateTask}
                disabled={isCreating || (
                  (pickupSource === 'custom' ? (!newTask.customPickupName || !newTask.customPickupAddress) : !newTask.pickupId) ||
                  (dropoffSource === 'custom' ? (!newTask.customDropoffName || !newTask.customDropoffAddress) : !newTask.dropoffId)
                )}
              >
                {isCreating ? (
                  <>
                    <div className="loading-spinner small" />
                    Skapar uppdrag...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Skapa uppdrag
                  </>
                )}
              </button>
            </div>
          )}

          {activeTab === 'queue' && (
            <div className="task-list">
              {pendingTasks.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/>
                      <path d="M16 7V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v3"/>
                    </svg>
                  </div>
                  <h3>Inga väntande uppdrag</h3>
                  <p>Skapa ett nytt uppdrag för att komma igång</p>
                  <button className="btn btn-secondary" onClick={() => setActiveTab('create')}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="12" y1="5" x2="12" y2="19"/>
                      <line x1="5" y1="12" x2="19" y2="12"/>
                    </svg>
                    Skapa uppdrag
                  </button>
                </div>
              ) : (
                pendingTasks.map(task => (
                  <div 
                    key={task.id}
                    className={`task-card ${selectedTask?.id === task.id ? 'selected' : ''}`}
                    onClick={() => onTaskSelect(task)}
                  >
                    <div className="task-card-header">
                      <span className="task-type">{taskTypeLabels[task.type]}</span>
                      <span 
                        className="task-priority"
                        style={{ color: priorityLabels[task.priority].color }}
                      >
                        {priorityLabels[task.priority].label}
                      </span>
                    </div>
                    
                    <div className="task-locations">
                      <div className="task-location pickup">
                        <div className="location-dot pickup" />
                        <span>{task.pickup.label}</span>
                      </div>
                      <div className="task-arrow">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"/>
                          <polyline points="12 5 19 12 12 19"/>
                        </svg>
                      </div>
                      <div className="task-location dropoff">
                        <div className="location-dot dropoff" />
                        <span>{task.dropoff.label}</span>
                      </div>
                    </div>

                    <div className="task-meta">
                      <span className="task-time">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {formatTimeAgo(task.createdAt)}
                      </span>
                      {task.distanceMeters && (
                        <span className="task-distance">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          </svg>
                          {Math.round(task.distanceMeters)}m
                        </span>
                      )}
                      <span className="task-duration">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M5 22h14"/>
                          <path d="M5 2h14"/>
                          <path d="M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22"/>
                        </svg>
                        ~{task.estimatedDuration} min
                      </span>
                    </div>

                    <div className="task-actions">
                      <button 
                        className="btn btn-auto-assign"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTaskAutoAssign(task.id);
                        }}
                        disabled={availableDrivers.length === 0}
                        title={availableDrivers.length === 0 ? 'Inga lediga förare' : 'Auto-tilldela'}
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                        </svg>
                        Auto
                      </button>
                      <select 
                        className="driver-select"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          if (e.target.value) {
                            onTaskAssign(task.id, e.target.value);
                            e.target.value = '';
                          }
                        }}
                        defaultValue=""
                      >
                        <option value="">Välj förare...</option>
                        {availableDrivers.map(driver => (
                          <option key={driver.id} value={driver.id}>
                            {driver.name} {driver.batteryLevel ? `(${driver.batteryLevel}%)` : ''}
                          </option>
                        ))}
                      </select>
                      <button 
                        className="btn-icon btn-cancel"
                        onClick={(e) => {
                          e.stopPropagation();
                          onTaskCancel(task.id);
                        }}
                        title="Avbryt"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"/>
                          <line x1="6" y1="6" x2="18" y2="18"/>
                        </svg>
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'active' && (
            <div className="task-list">
              {activeTasks.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <h3>Inga aktiva uppdrag</h3>
                  <p>Tilldela väntande uppdrag för att starta</p>
                </div>
              ) : (
                activeTasks.map(task => {
                  const driver = drivers.find(d => d.id === task.assignedDriverId);
                  
                  return (
                    <div 
                      key={task.id}
                      className={`task-card active ${selectedTask?.id === task.id ? 'selected' : ''}`}
                      onClick={() => onTaskSelect(task)}
                    >
                      <div className="task-card-header">
                        <span className="task-type">{taskTypeLabels[task.type]}</span>
                        <span 
                          className="task-status"
                          style={{ color: getStatusColor(task.status) }}
                        >
                          {getStatusLabel(task.status)}
                        </span>
                      </div>

                      {driver && (
                        <div className="task-driver">
                          <div className="driver-avatar">
                            {driver.name.charAt(0)}
                          </div>
                          <div className="driver-info">
                            <span className="driver-name">{driver.name}</span>
                            <span className="driver-status">{driver.status === 'moving' ? 'I rörelse' : 'Stillastående'}</span>
                          </div>
                          {task.etaMinutes && (
                            <div className="task-eta">
                              <span className="eta-label">ETA</span>
                              <span className="eta-value">{task.etaMinutes} min</span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="task-locations">
                        <div className={`task-location pickup ${task.currentStep === 'navigate_pickup' || task.currentStep === 'at_pickup' ? 'current' : 'done'}`}>
                          <div className="location-dot pickup" />
                          <span>{task.pickup.label}</span>
                        </div>
                        <div className="task-progress-line">
                          <div 
                            className="progress-fill"
                            style={{ 
                              width: task.currentStep === 'navigate_pickup' ? '0%' :
                                     task.currentStep === 'at_pickup' ? '25%' :
                                     task.currentStep === 'loading' ? '40%' :
                                     task.currentStep === 'navigate_dropoff' ? '60%' :
                                     task.currentStep === 'at_dropoff' ? '85%' :
                                     task.currentStep === 'done' ? '100%' : '0%'
                            }}
                          />
                        </div>
                        <div className={`task-location dropoff ${task.currentStep === 'navigate_dropoff' || task.currentStep === 'at_dropoff' ? 'current' : task.currentStep === 'done' ? 'done' : ''}`}>
                          <div className="location-dot dropoff" />
                          <span>{task.dropoff.label}</span>
                        </div>
                      </div>

                      <div className="task-actions">
                        <button 
                          className="btn btn-view-map"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTaskSelect(task);
                          }}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/>
                            <line x1="8" y1="2" x2="8" y2="18"/>
                            <line x1="16" y1="6" x2="16" y2="22"/>
                          </svg>
                          Visa på karta
                        </button>
                        <button 
                          className="btn-icon btn-cancel"
                          onClick={(e) => {
                            e.stopPropagation();
                            onTaskCancel(task.id);
                          }}
                          title="Avbryt"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {activeTab === 'history' && (
            <div className="task-list history">
              {completedTasks.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon">
                    <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                      <path d="M3 3v5h5"/>
                      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
                    </svg>
                  </div>
                  <h3>Ingen historik än</h3>
                  <p>Slutförda uppdrag visas här</p>
                </div>
              ) : (
                completedTasks.slice(0, 20).map(task => {
                  const driver = drivers.find(d => d.id === task.assignedDriverId);
                  
                  return (
                    <div 
                      key={task.id}
                      className={`task-card history ${task.status}`}
                      onClick={() => onTaskSelect(task)}
                    >
                      <div className="task-card-header">
                        <span className="task-type">{taskTypeLabels[task.type]}</span>
                        <span 
                          className="task-status"
                          style={{ color: getStatusColor(task.status) }}
                        >
                          {task.status === 'completed' ? '✓ ' : task.status === 'failed' ? '✗ ' : '⊘ '}
                          {getStatusLabel(task.status)}
                        </span>
                      </div>
                      
                      <div className="task-locations compact">
                        <span>{task.pickup.zoneName || task.pickup.zoneId}</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="5" y1="12" x2="19" y2="12"/>
                          <polyline points="12 5 19 12 12 19"/>
                        </svg>
                        <span>{task.dropoff.zoneName || task.dropoff.zoneId}</span>
                      </div>

                      <div className="task-meta">
                        {driver && <span className="task-driver-name">{driver.name}</span>}
                        {task.completedAt && <span>{formatTimeAgo(task.completedAt)}</span>}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DispatchPanel;
